import { useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Upload, FileUp, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function UploadDevisDialog({ projectId, onUploaded, trigger }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [model, setModel] = useState("claude");
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setModel("claude");
        setLoading(false);
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.error("Sélectionnez un fichier PDF");
            return;
        }
        setLoading(true);
        try {
            const devis = await api.uploadDevis(projectId, file, model);
            toast.success(
                `Devis "${devis.contractor_name}" analysé (${devis.line_items.length} postes)`
            );
            setOpen(false);
            reset();
            onUploaded && onUploaded(devis);
        } catch (e) {
            toast.error(
                "Échec de l'analyse. Vérifiez le PDF et réessayez."
            );
            setLoading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files?.length) {
            const f = e.dataTransfer.files[0];
            if (f.type !== "application/pdf") {
                toast.error("Le fichier doit être un PDF");
                return;
            }
            setFile(f);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <DialogTrigger asChild>
                {trigger || (
                    <Button
                        data-testid="upload-devis-btn"
                        className="rounded-none bg-accent hover:bg-accent-hover"
                    >
                        <Upload className="w-4 h-4 mr-2" strokeWidth={2} />
                        Importer un devis PDF
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-accent" strokeWidth={1.6} />
                        Importer un devis PDF
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="stat-label">Modèle d'analyse</Label>
                        <Select value={model} onValueChange={setModel}>
                            <SelectTrigger
                                data-testid="upload-model-select"
                                className="rounded-none mt-1.5"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                <SelectItem value="claude">
                                    Claude Sonnet 4.5 (recommandé)
                                </SelectItem>
                                <SelectItem value="openai">GPT-5.2</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => inputRef.current?.click()}
                        data-testid="upload-dropzone"
                        className="border-2 border-dashed border-ink/30 hover:border-accent transition-colors cursor-pointer p-8 text-center bg-[#FAFAFA]"
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) =>
                                setFile(e.target.files?.[0] || null)
                            }
                            data-testid="upload-file-input"
                        />
                        <FileUp
                            className="w-10 h-10 mx-auto mb-3 text-ink-soft"
                            strokeWidth={1.5}
                        />
                        {file ? (
                            <div>
                                <div className="font-medium text-ink">
                                    {file.name}
                                </div>
                                <div className="text-xs text-ink-soft mt-1">
                                    {(file.size / 1024).toFixed(0)} Ko —
                                    Cliquez pour changer
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="font-medium text-ink">
                                    Glissez-déposez votre PDF ici
                                </div>
                                <div className="text-xs text-ink-soft mt-1">
                                    ou cliquez pour sélectionner
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="text-xs text-ink-soft border-l-2 border-accent pl-3">
                        L'IA extrait automatiquement les postes, quantités,
                        prix unitaires, et tags pour la détection de doublons.
                        Comptez 15 à 45 secondes selon la taille du devis.
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                        disabled={loading}
                    >
                        Annuler
                    </Button>
                    <Button
                        data-testid="upload-submit"
                        className="rounded-none bg-accent hover:bg-accent-hover"
                        onClick={handleSubmit}
                        disabled={loading || !file}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyse en cours…
                            </>
                        ) : (
                            <>
                                <Sparkles
                                    className="w-4 h-4 mr-2"
                                    strokeWidth={1.6}
                                />
                                Analyser le devis
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
